import { useState, type FC } from 'react';
import { Flex, Popconfirm, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Link, Space, Text } from '@g2u/ui-kit';
import { ServiceTypeModal, SkillsetModal } from 'components';

import { ServiceTypeAvailability, SkillLevel, TechnicianServiceType, TechnicianSkillSet } from 'types';
import { useTechnicianSkillSets } from '../hooks/useTechnicianSkillSets';
import { useTechnicianServiceTypes } from '../hooks/useTechnicianServiceTypes';

type Props = Readonly<{
  technicianId?: string;
}>;

const skillLevelLabels: Record<SkillLevel, string> = {
  [SkillLevel.None]: 'None',
  [SkillLevel.Proficient]: 'Proficient',
  [SkillLevel.Expert]: 'Expert',
};

const serviceTypeAvailabilityLabels: Record<ServiceTypeAvailability, string> = {
  [ServiceTypeAvailability.NotAvailable]: 'Not Available',
  [ServiceTypeAvailability.Available]: 'Available',
};

export const TechnicianSkillsetsServiceTypes: FC<Props> = ({ technicianId }) => {
  const [showSkillsetModal, setShowSkillsetModal] = useState(false);
  const [editedSkillset, setEditedSkillset] = useState<TechnicianSkillSet | null>(null);
  const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);
  const [editedServiceType, setEditedServiceType] = useState<TechnicianServiceType | null>(null);

  const {
    skillSets,
    isSkillSetsLoading,
    assignableSkillSets,
    createSkillSet,
    isSkillSetCreating,
    updateSkillSet,
    isSkillSetUpdating,
    deleteSkillSet,
  } = useTechnicianSkillSets(technicianId);
  const {
    serviceTypes,
    isServiceTypesLoading,
    assignableServiceTypes,
    createServiceType,
    isServiceTypeCreating,
    updateServiceType,
    isServiceTypeUpdating,
    deleteServiceType,
  } = useTechnicianServiceTypes(technicianId);

  const handleSkillsetModalClose = () => {
    setShowSkillsetModal(false);
    setEditedSkillset(null);
  };

  const handleSkillsetModalSubmit = (values: SkillsetModal.FormValues) => {
    if (editedSkillset) {
      updateSkillSet(
        { techId: Number(technicianId), technicianSkillSetId: values.technicianSkillSetId as number, level: values.level },
        { onSuccess: handleSkillsetModalClose },
      );
    } else {
      createSkillSet(
        { techId: Number(technicianId), skillset_ID: values.skillset_ID, level: values.level },
        { onSuccess: handleSkillsetModalClose },
      );
    }
  };

  const handleServiceTypeModalClose = () => {
    setShowServiceTypeModal(false);
    setEditedServiceType(null);
  };

  const handleServiceTypeModalSubmit = (values: ServiceTypeModal.FormValues) => {
    if (editedServiceType) {
      updateServiceType(
        { techId: Number(technicianId), technicianServiceTypeId: values.technicianServiceTypeId as number, status: values.status },
        { onSuccess: handleServiceTypeModalClose },
      );
    } else {
      createServiceType(
        { techId: Number(technicianId), service_type_ID: values.service_type_ID, status: values.status },
        { onSuccess: handleServiceTypeModalClose },
      );
    }
  };

  const serviceTypeColumns: ColumnsType<TechnicianServiceType> = [
    {
      key: 'skill',
      title: 'Skill',
      dataIndex: 'type_description',
    },
    {
      key: 'available',
      title: 'Available',
      dataIndex: 'service_status',
      render: (status: ServiceTypeAvailability) => serviceTypeAvailabilityLabels[status],
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, record) => (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
          <Link.$
            onClick={() => {
              setEditedServiceType(record);
              setShowServiceTypeModal(true);
            }}
          >
            Edit
          </Link.$>
          <Popconfirm
            icon={false}
            title={<Text.$>Delete the Service Type</Text.$>}
            description="Are you sure you want to delete this Service Type?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => deleteServiceType({ techId: Number(technicianId), technicianServiceTypeId: record.ID })}
          >
            <Link.$>
              <Text.$ type="danger">Delete</Text.$>
            </Link.$>
          </Popconfirm>
        </Space.$>
      ),
    },
  ];

  const skillSetColumns: ColumnsType<TechnicianSkillSet> = [
    {
      key: 'skillset',
      title: 'Skillset',
      dataIndex: 'description',
    },
    {
      key: 'skillLevel',
      title: 'Skill Level',
      dataIndex: 'level',
      render: (level: SkillLevel) => skillLevelLabels[level],
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, record) => (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
          <Link.$
            onClick={() => {
              setEditedSkillset(record);
              setShowSkillsetModal(true);
            }}
          >
            Edit
          </Link.$>
          <Popconfirm
            icon={false}
            title={<Text.$>Delete the skill</Text.$>}
            description="Are you sure you want to delete this skill?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => deleteSkillSet({ techId: Number(technicianId), technicianSkillSetId: record.ID })}
          >
            <Link.$>
              <Text.$ type="danger">Delete</Text.$>
            </Link.$>
          </Popconfirm>
        </Space.$>
      ),
    },
  ];

  return (
    <Flex gap={24} align="flex-start">
      <Flex vertical gap={16} style={{ flex: 1 }}>
        <Text.$ strong size={Text.Size.LG}>
          Skills
        </Text.$>
        <Table
          rowKey={(record) => record.ID}
          loading={isSkillSetsLoading}
          columns={skillSetColumns}
          dataSource={skillSets ?? []}
          pagination={false}
        />
        <Button.$
          type={Button.Type.Primary}
          icon={<PlusOutlined />}
          onClick={() => setShowSkillsetModal(true)}
          dataTestId="new-skillset-button"
        >
          New Skillset
        </Button.$>
      </Flex>
      <Flex vertical gap={16} style={{ flex: 1 }}>
        <Text.$ strong size={Text.Size.LG}>
          Service Type
        </Text.$>
        <Table
          rowKey={(record) => record.ID}
          loading={isServiceTypesLoading}
          columns={serviceTypeColumns}
          dataSource={serviceTypes ?? []}
          pagination={false}
        />
        <Button.$
          type={Button.Type.Primary}
          icon={<PlusOutlined />}
          onClick={() => setShowServiceTypeModal(true)}
          dataTestId="new-service-type-button"
        >
          New Service Type
        </Button.$>
      </Flex>
      <SkillsetModal
        skillset={editedSkillset}
        assignableSkillSets={assignableSkillSets ?? []}
        isOpen={showSkillsetModal}
        isLoading={isSkillSetCreating || isSkillSetUpdating}
        onSubmit={handleSkillsetModalSubmit}
        onClose={handleSkillsetModalClose}
      />
      <ServiceTypeModal
        serviceType={editedServiceType}
        assignableServiceTypes={assignableServiceTypes ?? []}
        isOpen={showServiceTypeModal}
        isLoading={isServiceTypeCreating || isServiceTypeUpdating}
        onSubmit={handleServiceTypeModalSubmit}
        onClose={handleServiceTypeModalClose}
      />
    </Flex>
  );
};
